import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Check, Loader2, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { formatUnits, getAddress } from 'viem';
import {
    switchChain,
    waitForTransactionReceipt,
    writeContract,
} from 'wagmi/actions';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
    CRONOS_CHAIN_ID,
    HYPER_EVM_CHAIN_ID,
    useWallet,
    walletWagmiConfig,
} from '@/features/wallet';
import {
    describeErrorText,
    getErrorMessage,
    isUserRejection,
} from '@/lib/errors';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';
import { queryKeys } from '@/lib/query';
import { cn, shortenAddress } from '@/lib/utils';

import { ensureHyperEvmChain } from '../hooks/hyperEvmChain';
import { useLegacyWalletFunds } from '../hooks/useLegacyWalletFunds';
import type { LegacyBalance } from '../hooks/useLegacyWalletFunds';

/**
 * The owner-only escape hatches every deployed CustodialWallet exposes. They
 * require no signature, no manager involvement and work regardless of pause
 * state, and the wallet's owner (the connected EOA) never changes, so the user
 * can drain a wallet on a retired manager entirely by themselves.
 *
 * Local to this file so deleting it, the hook and the two mounts removes the
 * whole feature.
 */
const LEGACY_WALLET_WITHDRAW_ABI = [
    {
        type: 'function',
        name: 'withdrawNative',
        stateMutability: 'nonpayable',
        inputs: [
            { name: '_to', type: 'address' },
            { name: '_amount', type: 'uint256' },
        ],
        outputs: [],
    },
    {
        type: 'function',
        name: 'withdrawErc20',
        stateMutability: 'nonpayable',
        inputs: [
            { name: '_token', type: 'address' },
            { name: '_to', type: 'address' },
            { name: '_amount', type: 'uint256' },
        ],
        outputs: [],
    },
] as const;

const NO_GAS_MESSAGE =
    'You need a small amount of HYPE on your connected wallet for gas.';
const TOAST_DURATION = 10_000;

type StepStatus = 'pending' | 'sending' | 'confirmed' | 'failed';

interface WithdrawStep {
    key: string;
    symbol: string;
    decimals: number;
    amount: bigint;
    status: StepStatus;
}

/** A list row: a live step during a run, or a plain balance before one. */
type Row = Omit<WithdrawStep, 'status'> & { status: StepStatus | null };

/** Compact display of a token amount (trailing zeros dropped). */
function formatAmount(amount: bigint, decimals: number): string {
    const text = formatUnits(amount, decimals);
    if (!text.includes('.')) return text;
    const [whole, fraction] = text.split('.');
    const trimmed = fraction.slice(0, 6).replace(/0+$/, '');
    return trimmed ? `${whole}.${trimmed}` : whole;
}

/**
 * Only explicit cannot-pay-for-gas errors get the friendly gas message: viem's
 * InsufficientFundsError text and geth's gas-allowance variant. Relabelling
 * anything else as a gas problem would send the user off to top up HYPE for
 * nothing, so the rest (a revert during estimation, a stale amount, an ABI
 * mismatch) goes to the shared presenter instead of into the panel verbatim.
 */
function readableError(error: unknown): string {
    const text = getErrorMessage(error);
    if (
        /insufficient funds for|exceeds the balance of the account|gas required exceeds allowance/i.test(
            text,
        )
    ) {
        return NO_GAS_MESSAGE;
    }
    return describeErrorText(error, { operation: 'Withdrawal' });
}

/**
 * Waits for a receipt and rejects both reverts AND wallet-side replacements
 * that changed the transaction. Wallet "cancel" mines a replacement
 * transaction whose receipt is SUCCESSFUL, so without inspecting `onReplaced`
 * a cancelled withdrawal would be labelled as moved. A `repriced` replacement
 * (speed-up) keeps the original calldata and is accepted.
 *
 * Local rather than in the shared `waitForSuccessfulHyperEvmReceipt`: the other
 * custodial flows have their own recovery semantics and were reviewed against
 * the shared helper's behavior.
 */
async function waitForLegacyWithdrawReceipt(
    hash: `0x${string}`,
    label: string,
): Promise<void> {
    let replacement: 'repriced' | 'cancelled' | 'replaced' | null = null;
    const receipt = await waitForTransactionReceipt(walletWagmiConfig, {
        hash,
        chainId: HYPER_EVM_CHAIN_ID,
        onReplaced: (response) => {
            replacement = response.reason;
        },
    });
    if (replacement && replacement !== 'repriced') {
        throw new Error(
            replacement === 'cancelled'
                ? `${label} was cancelled in your wallet.`
                : `${label} was replaced by another transaction in your wallet.`,
        );
    }
    if (receipt.status !== 'success') {
        throw new Error(`${label} transaction reverted.`);
    }
}

/**
 * One owner-only withdrawal on the OLD wallet. The two entrypoints take
 * different arguments, so they stay separate calls rather than one call with a
 * computed function name.
 */
async function submitLegacyWithdraw(input: {
    eoa: `0x${string}`;
    source: `0x${string}`;
    destination: `0x${string}`;
    balance: LegacyBalance;
}): Promise<`0x${string}`> {
    const { eoa, source, destination, balance } = input;
    const base = {
        account: eoa,
        address: source,
        abi: LEGACY_WALLET_WITHDRAW_ABI,
        chainId: HYPER_EVM_CHAIN_ID,
    } as const;
    if (balance.address) {
        return writeContract(walletWagmiConfig, {
            ...base,
            functionName: 'withdrawErc20',
            args: [balance.address, destination, balance.amount],
        });
    }
    return writeContract(walletWagmiConfig, {
        ...base,
        functionName: 'withdrawNative',
        args: [destination, balance.amount],
    });
}

const STATUS_LABEL: Record<StepStatus, string> = {
    pending: 'Waiting',
    sending: 'Confirm in wallet',
    confirmed: 'Moved',
    failed: 'Failed',
};

/**
 * Self-serve recovery of funds left on a user's custodial wallet from the
 * PREVIOUS CustodialWalletManager, after a switchover.
 *
 * The connected EOA owns that old wallet and submits every transaction itself:
 * no backend call, no signer, no contract change. One transaction per asset,
 * each confirmed before the next, with native left for last so the gas it pays
 * for cannot change what the token legs move.
 *
 * Silent unless `lib/config/custodialManagers.ts` declares a `previous` manager
 * for HyperEVM and the connected wallet actually has something on it.
 */
const LegacyWalletWithdraw: React.FC = () => {
    const { address } = useWallet();
    const queryClient = useQueryClient();
    const { eligible, oldWallet, newWallet, balances, refetch } =
        useLegacyWalletFunds();
    const [expanded, setExpanded] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [steps, setSteps] = useState<WithdrawStep[]>([]);
    const [error, setError] = useState<string | null>(null);

    const setStepStatus = useCallback((key: string, status: StepStatus) => {
        setSteps((prev) =>
            prev.map((step) => (step.key === key ? { ...step, status } : step)),
        );
    }, []);

    const run = useCallback(async () => {
        if (isRunning || !address) return;
        setIsRunning(true);
        setError(null);
        setSteps([]);
        let switchedToHyperEvm = false;
        let movedAnything = false;
        try {
            // amounts are re-read at click time: the displayed snapshot can be
            // up to 30s old, and a stale amount would revert the withdrawal.
            const fresh = await refetch();
            if (
                !fresh?.oldWallet ||
                !fresh.newWallet ||
                fresh.balances.length === 0
            ) {
                return;
            }
            const eoa = getAddress(address);
            const source = fresh.oldWallet;
            const destination = fresh.newWallet;

            // an EOA with literally nothing cannot pay for any of this, and the
            // failure would otherwise land as an opaque estimation error.
            const gas = await hyperEvmPublicClient.getBalance({ address: eoa });
            if (gas === 0n) {
                throw new Error(NO_GAS_MESSAGE);
            }

            await ensureHyperEvmChain();
            switchedToHyperEvm = true;

            const plan: WithdrawStep[] = fresh.balances.map(
                (balance: LegacyBalance) => ({
                    key: balance.address ?? 'native',
                    symbol: balance.symbol,
                    decimals: balance.decimals,
                    amount: balance.amount,
                    status: 'pending' as const,
                }),
            );
            setSteps(plan);
            setExpanded(true);

            for (const balance of fresh.balances) {
                const key = balance.address ?? 'native';
                setStepStatus(key, 'sending');
                try {
                    const hash = await submitLegacyWithdraw({
                        eoa,
                        source,
                        destination,
                        balance,
                    });
                    await waitForLegacyWithdrawReceipt(
                        hash,
                        `${balance.symbol} withdrawal`,
                    );
                    movedAnything = true;
                    setStepStatus(key, 'confirmed');
                } catch (stepError) {
                    if (isUserRejection(stepError)) {
                        // cancelling is not a failure: drop back to the offer so
                        // the user can start again whenever they want.
                        setSteps([]);
                        return;
                    }
                    setStepStatus(key, 'failed');
                    throw stepError;
                }
            }

            toast.success('Your previous wallet has been emptied.', {
                duration: TOAST_DURATION,
            });
        } catch (runError) {
            if (!isUserRejection(runError)) {
                console.error('[LegacyWalletWithdraw] failed:', runError);
                setError(readableError(runError));
            }
        } finally {
            // each leg is its own transaction, so a partial run still changed the
            // balances the banner shows.
            await refetch().catch(() => null);
            if (movedAnything) {
                // the destination is the live trading wallet, so the panels
                // reading GET /speed/balance are stale the moment a leg lands.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.balance(),
                });
            }
            setIsRunning(false);
            if (switchedToHyperEvm) {
                // the rest of the app gates on Cronos (runtimeMode falls back to
                // demo off it), so ANY run that switched must switch back, even
                // one the user cancelled at the first prompt. Best effort: the
                // next deposit or auth flow runs ensureCronos anyway.
                switchChain(walletWagmiConfig, {
                    chainId: CRONOS_CHAIN_ID,
                }).catch(() => undefined);
            }
        }
    }, [address, isRunning, queryClient, refetch, setStepStatus]);

    if (!eligible && !isRunning) return null;

    // one shape for the list whether a run is in progress (steps carry a status)
    // or the banner is just showing what is there.
    const idleRows: Row[] = balances.map((balance) => ({
        key: balance.address ?? 'native',
        symbol: balance.symbol,
        decimals: balance.decimals,
        amount: balance.amount,
        status: null,
    }));
    const rows: Row[] = steps.length > 0 ? steps : idleRows;

    return (
        <div
            role="status"
            className="mb-4 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
        >
            <div className="flex items-start gap-2">
                <ArrowRightLeft
                    size={14}
                    className="mt-0.5 shrink-0 text-primary"
                />
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-primary">
                        You have funds on your previous Snappy wallet
                    </p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Move them to your current wallet. You submit the
                        transactions from your connected wallet (one per asset),
                        so you need a little HYPE for gas.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded((open) => !open)}
                    className="shrink-0 text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                >
                    {expanded ? 'Hide' : 'Details'}
                </button>
            </div>

            {expanded && (
                <ul className="mt-2 space-y-1 border-t border-primary/20 pt-2">
                    {rows.map((row) => {
                        const { key, status } = row;
                        return (
                            <li
                                key={key}
                                className="flex items-center justify-between gap-2 text-[11px]"
                            >
                                <span className="font-mono tabular-nums text-foreground">
                                    {formatAmount(row.amount, row.decimals)}{' '}
                                    {row.symbol}
                                </span>
                                {status && (
                                    <span
                                        className={cn(
                                            'flex items-center gap-1',
                                            status === 'confirmed' &&
                                                'text-primary',
                                            status === 'failed' &&
                                                'text-destructive',
                                            (status === 'pending' ||
                                                status === 'sending') &&
                                                'text-muted-foreground',
                                        )}
                                    >
                                        {status === 'sending' && (
                                            <Loader2
                                                size={11}
                                                className="animate-spin"
                                            />
                                        )}
                                        {status === 'confirmed' && (
                                            <Check size={11} />
                                        )}
                                        {status === 'failed' && <X size={11} />}
                                        {STATUS_LABEL[status]}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {error && (
                <p className="mt-2 text-[11px] leading-relaxed text-destructive">
                    {error}
                </p>
            )}

            <Button
                variant="outline"
                size="sm"
                disabled={isRunning}
                onClick={() => void run()}
                className="mt-2.5 w-full gap-1 border-primary/30 text-primary hover:border-primary/50 hover:bg-primary/10"
            >
                {isRunning ? (
                    <Loader2 size={14} className="animate-spin" />
                ) : (
                    <ArrowRightLeft size={14} />
                )}
                {isRunning ? 'Moving funds' : 'Move funds to my new wallet'}
            </Button>

            {oldWallet && newWallet && (
                <p className="mt-1.5 text-[10px] text-muted-foreground">
                    From {shortenAddress(oldWallet)} to{' '}
                    {shortenAddress(newWallet)}
                </p>
            )}
        </div>
    );
};

LegacyWalletWithdraw.displayName = 'LegacyWalletWithdraw';

export default LegacyWalletWithdraw;
